import { cn } from "@/lib/utils";
import { SelectChevronIcon } from "@/lib/svg-icons";
import {
  Badge,
  Button,
  Label,
  MultiSelectFilter,
  type MultiSelectFilterOption,
} from "@capacit-com/lara-law-shared-ui";

export type MultiSelectFieldProps<T extends object | string> = {
  options: T[];
  value?: T[];
  placeholder?: string;
  optionKey?: keyof T;
  secondaryOptionKey?: keyof T;
  groupKey?: keyof T;
  label?: string;
  disabled?: boolean;
  dividerIndexes?: number[];
  onChange?: (selected: T) => void;
  onClear?: () => void;
  align?: "center" | "end" | "start" | undefined;
  selectedOptionKey?: keyof T;
  className?: string;
  onlyShowSelectedNumber?: boolean;
};

const MultiSelectField = <T extends object | string>({
  options,
  value = [],
  optionKey,
  secondaryOptionKey,
  groupKey,
  placeholder = "Select options",
  label,
  disabled,
  onChange,
  onClear,
  align = "start",
  selectedOptionKey,
  className,
  onlyShowSelectedNumber = false,
}: MultiSelectFieldProps<T>) => {
  const getOptionLabel = (option: T): string =>
    typeof option === "object" && optionKey
      ? String(option[optionKey])
      : String(option);

  const getSelectedOptionLabel = (option: T): string =>
    typeof option === "object" && selectedOptionKey
      ? String(option[selectedOptionKey])
      : String(option);

  const getGroupLabel = (option: T): string | undefined =>
    groupKey && typeof option === "object"
      ? String(option[groupKey])
      : undefined;

  const getSecondaryLabel = (option: T): string | undefined =>
    secondaryOptionKey && typeof option === "object"
      ? String(option[secondaryOptionKey as keyof T])
      : undefined;

  // `optionKey` is the stable identity for an option — used as the value
  // threaded through MultiSelectFilter, as the selection key, and for
  // reverse-lookups back to T. `selectedOptionKey` is display-only: it only
  // controls the chip label rendered in the trigger button.
  const filterOptions: MultiSelectFilterOption[] = options.map((opt) => ({
    value: getOptionLabel(opt),
    label: getOptionLabel(opt),
    group: getGroupLabel(opt),
    secondaryLabel: getSecondaryLabel(opt),
  }));

  // Derive the selected string values using the same key as filterOptions.value
  // (i.e. getOptionLabel), so MultiSelectFilter can match them correctly.
  const selectedValues = value.map((v) => getOptionLabel(v));

  // The shared primitive calls onSelectionChange with the full new array.
  // Diff against the current selection to find the single toggled option,
  // then forward it to the original onChange(T) callback.
  // The internal clear button is hidden when onClear is absent (hideClear prop),
  // so newValues.length === 0 only happens when onClear is provided.
  const handleSelectionChange = (newValues: string[]) => {
    if (newValues.length === 0 && selectedValues.length > 0) {
      onClear?.();
      return;
    }

    const toggled =
      newValues.find((v) => !selectedValues.includes(v)) ??
      selectedValues.find((v) => !newValues.includes(v));
    if (!toggled) return;
    const match = options.find((opt) => getOptionLabel(opt) === toggled);
    if (match) onChange?.(match);
  };

  const renderTrigger = (open: boolean) => (
    <Button
      variant="outline"
      role="combobox"
      aria-expanded={open}
      className={cn(
        "flex min-h-9 h-auto items-start justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs ring-offset-background placeholder:text-muted-foreground focus:outline-hidden focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
        "focus:ring-1 focus:ring-primary focus-visible:ring-1 focus-visible:ring-primary hover:bg-accent/50",
        className
      )}
      disabled={disabled}
      dir="ltr"
      aria-autocomplete="none"
    >
      {value.length > 0 && !onlyShowSelectedNumber ? (
        <div className="flex flex-wrap gap-1 pr-2 max-w-[calc(100%-20px)] min-w-0 overflow-hidden">
          {value.map((item) => (
            <Badge
              key={getOptionLabel(item)}
              variant="secondary"
              className="max-w-full min-w-0 truncate"
            >
              {selectedOptionKey
                ? getSelectedOptionLabel(item)
                : getOptionLabel(item)}
            </Badge>
          ))}
        </div>
      ) : value.length > 0 && onlyShowSelectedNumber ? (
        <Badge variant="secondary" className="max-w-full min-w-0 truncate">
          {value.length === 1
            ? (value[0] as string)
            : `${value.length} valgte`}
        </Badge>
      ) : (
        <span className="font-normal pointer-events-none">{placeholder}</span>
      )}
      <SelectChevronIcon />
    </Button>
  );

  return (
    <>
      <div className={cn("flex justify-between pt-2")}>
        {label && <Label>{label}</Label>}
        {onClear && (
          <Label
            className="text-muted-foreground text-xs cursor-pointer"
            onClick={onClear}
          >
            Nulstil
          </Label>
        )}
      </div>
      <MultiSelectFilter
        title=""
        options={filterOptions}
        selectedValues={selectedValues}
        onSelectionChange={handleSelectionChange}
        align={align}
        renderTrigger={renderTrigger}
        contentClassName="w-full"
        searchPlaceholder="Søg..."
        emptyLabel="Vi fandt ingen mulige."
        clearLabel="Nulstil"
        hideClear={!onClear}
      />
    </>
  );
};

export default MultiSelectField;
